import { axios } from '@Api/axiosClient';
import type { IdvResponse } from '@Types/IdvResponse.interface';
import type { GetMobileSessionResponse } from '@Types/GetMobileSessionResponse.interface';
import type { StartCaptureFlowRequest } from '@Types/StartCaptureFlowRequest.interface';
import type { IdvCaptureOptions } from '@Types/IdvCaptureOptions.interface';
import { SessionExpiredError, isSessionGoneResponse } from '@Utils/sessionExpired';

const baseUrl: string = '/api/idv';

/**
 * Which capture steps this flow still collects itself.
 *
 * Falls back to collecting the document type ourselves when the call fails: that is the
 * Intellicheck behaviour, and it is the only answer that is never wrong to act on — showing the
 * picker on a Socure flow is a redundant screen, skipping it on an Intellicheck flow is a capture
 * that cannot start.
 */
export const getCaptureOptions = async (): Promise<IdvCaptureOptions> => {
    try {
        const response = await axios.get<IdvCaptureOptions>(`${baseUrl}/GetCaptureOptions`);
        return response.data;
    } catch (error) {
        console.error('[Auth MFE] GetCaptureOptions failed:', error);
        return { vendorCollectsDocumentType: false };
    }
};

/** @deprecated Use StartCaptureFlowRequest from @Types/StartCaptureFlowRequest.interface */
export type StartCaptureFlowPayload = StartCaptureFlowRequest;

export const beginIdentityVerification = async (payload: StartCaptureFlowRequest): Promise<IdvResponse> => {
    const response = await axios.post(`${baseUrl}/StartCaptureFlow`, payload);
    return response?.data;
};

export { type GetMobileSessionResponse } from '@Types/GetMobileSessionResponse.interface';
export { type StartCaptureFlowRequest } from '@Types/StartCaptureFlowRequest.interface';
export { type IdvCaptureOptions } from '@Types/IdvCaptureOptions.interface';

// A 404 means the session is gone and throws SessionExpiredError. Any other failure is
// logged and resolves undefined, so the router can try its fallback path (DS-3208).
export const getMobileSession = async (): Promise<GetMobileSessionResponse | undefined> => {
    try {
        const response = await axios.get<GetMobileSessionResponse>(`${baseUrl}/GetMobileSession`);
        return response.data;
    } catch (error) {
        if (isSessionGoneResponse(error)) {
            throw new SessionExpiredError('GetMobileSession');
        }
        console.error('[Auth MFE] GetMobileSession failed:', error);
        return undefined;
    }
};

export const getCurrentClientId = async () => {
    try
    {
        const response = await axios.get(`${baseUrl}/GetCurrentClientId`);
        return response.data;
    }
    catch (error)
    {
        console.error(error);
        return undefined;
    }
}

export const processPermissionsDeniedApi = async (): Promise<void> => {
    await axios.post(`${baseUrl}/ProcessPermissionsDenied`);
};